import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button, Form, Modal, Spinner, Alert, Badge } from 'react-bootstrap';
import {
  FaFileInvoice,
  FaSearch,
  FaCalendarAlt,
  FaFilter,
  FaPrint,
  FaDownload,
} from 'react-icons/fa';
import appConstants from '../constants/appConstants';
import Layout from '../components/Layout';
import '../pages/AccountStatementPrint.css';

const getAuthToken = () => localStorage.getItem('authToken');

const AccountStatement = () => {
  const [accountId, setAccountId] = useState('');
  const [accountName, setAccountName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [statement, setStatement] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [filteredAccounts, setFilteredAccounts] = useState([]);
  const [openingBalance, setOpeningBalance] = useState(0);
  const [error, setError] = useState('');
  const [showAccountDropdown, setShowAccountDropdown] = useState(false);
  const [statementFetched, setStatementFetched] = useState(false);
  const accountDropdownRef = useRef(null);

  useEffect(() => {
    const fetchAccounts = async () => {
      const token = getAuthToken();
      if (!token) return;
      try {
        const response = await axios.get(
          `${appConstants.API_ROOT_URL}get-accounts?all=1`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        setAccounts(response.data.data || []);
      } catch (error) {
        console.error('Error fetching accounts', error);
        setError('Failed to load accounts');
      }
    };
    fetchAccounts();
    setDateRange('thisMonth');
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        accountDropdownRef.current &&
        !accountDropdownRef.current.contains(event.target)
      ) {
        setShowAccountDropdown(false);
      }
    };

    if (showAccountDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showAccountDropdown]);

  const handleAccountChange = (e) => {
    const input = e.target.value;
    setAccountName(input);
    setShowAccountDropdown(true);

    if (input.trim() === '') {
      setFilteredAccounts([]);
      setAccountId('');
    } else {
      const matches = accounts.filter((acc) =>
        acc.name.toLowerCase().includes(input.toLowerCase()),
      );
      setFilteredAccounts(matches);
    }
  };

  const selectAccount = (account) => {
    setAccountId(account.id);
    setAccountName(account.name);
    setFilteredAccounts([]);
    setShowAccountDropdown(false);
  };

  const fetchStatement = async () => {
    if (!accountId) {
      setError('Please select a valid account');
      return;
    }
    if (!startDate || !endDate) {
      setError('Please select date range');
      return;
    }

    const token = getAuthToken();
    if (!token) {
      setError('Unauthorized! Please log in.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const response = await axios.post(
        `${appConstants.API_ROOT_URL}get-account-statement-v2`,
        {
          account_id: accountId,
          start_date: startDate,
          end_date: endDate,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.data?.status === 'success') {
        setStatement(response.data.transactions || []);
        setOpeningBalance(parseFloat(response.data.opening_balance) || 0);
        setAccountName(response.data.account_name || accountName);
        setStatementFetched(true);
        setShowModal(false);
      } else {
        setError(response.data?.message || 'Failed to fetch statement');
      }
    } catch (error) {
      setError('Error fetching account statement');
      console.error(error);
    }
    setLoading(false);
  };

  const setDateRange = (range) => {
    const today = new Date();
    let start, end;
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth();

    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-indexed, so we add 1
      const day = String(date.getDate()).padStart(2, '0'); // Ensure 2-digit day format
      return `${year}-${month}-${day}`;
    };

    switch (range) {
      case 'today':
        start = end = formatDate(today);
        break;
      case 'thisMonth':
        start = formatDate(new Date(currentYear, currentMonth, 1));
        end = formatDate(new Date(currentYear, currentMonth + 1, 0));
        break;
      case 'lastYear':
        const shift = -1;
        if (currentMonth >= 3) {
          // On or after April 1, financial year starts this year
          start = formatDate(new Date(currentYear + shift, 3, 1)); // April 1, current year
          end = formatDate(new Date(currentYear + 1 + shift, 2, 31)); // March 31, next year
        } else {
          // Before April 1, financial year started last year
          start = formatDate(new Date(currentYear - 1 + shift, 3, 1)); // April 1, last year
          end = formatDate(new Date(currentYear + shift, 2, 31)); // March 31, current year
        }
        break;
      case 'thisFinancialYear':
        if (currentMonth >= 3) {
          // On or after April 1, financial year starts this year
          start = formatDate(new Date(currentYear, 3, 1)); // April 1, current year
          end = formatDate(new Date(currentYear + 1, 2, 31)); // March 31, next year
        } else {
          // Before April 1, financial year started last year
          start = formatDate(new Date(currentYear - 1, 3, 1)); // April 1, last year
          end = formatDate(new Date(currentYear, 2, 31)); // March 31, current year
        }
        break;
      default:
        return;
    }

    setStartDate(start);
    setEndDate(end);
  };

  const formatDisplayDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const capitalizeFirstLetter = (string) => {
    if (!string) return '';
    return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
  };

  const getEntryTypeColor = (type) => {
    switch (type?.toLowerCase()) {
      case 'receipt':
        return '#28a745';
      case 'payment':
        return '#dc3545';
      case 'journal':
        return '#6c757d';
      case 'contra':
        return '#17a2b8';
      default:
        return '#6c757d';
    }
  };

  const getEntryTypeBadge = (type) => {
    const colors = {
      receipt: 'success',
      payment: 'danger',
      journal: 'secondary',
      contra: 'info',
    };
    return colors[type?.toLowerCase()] || 'secondary';
  };

  const calculateRunningBalance = () => {
    let balance = openingBalance;
    return statement.map((entry) => {
      const change =
        entry.debit_credit === 'D'
          ? parseFloat(entry.amount)
          : -parseFloat(entry.amount);
      balance += change;
      return { ...entry, runningBalance: balance };
    });
  };

  const getTotals = () => {
    const totalDebit = statement
      .filter((e) => e.debit_credit === 'D')
      .reduce((sum, e) => sum + parseFloat(e.amount || 0), 0);

    const totalCredit = statement
      .filter((e) => e.debit_credit === 'C')
      .reduce((sum, e) => sum + parseFloat(e.amount || 0), 0);

    const closingBalance = openingBalance + totalDebit - totalCredit;

    return { totalDebit, totalCredit, closingBalance };
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    const totals = getTotals();
    const statementsWithBalance = calculateRunningBalance();

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Account Statement - ${accountName}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            h3 { text-align: center; margin-bottom: 5px; }
            .header-info { text-align: center; margin-bottom: 20px; font-size: 14px; }
            table { width: 100%; border-collapse: collapse; margin-top: 10px; }
            th, td { border: 1px solid #000; padding: 8px; text-align: left; }
            th { background: #f0f0f0; font-weight: bold; }
            .text-right { text-align: right; }
            .text-center { text-align: center; }
            .bold { font-weight: bold; }
            @media print { body { margin: 10px; } }
          </style>
        </head>
        <body>
          <h3>Account Statement</h3>
          <div class="header-info">
            <strong>${accountName}</strong><br>
            Period: ${formatDisplayDate(startDate)} to ${formatDisplayDate(
              endDate,
            )}
          </div>
          <table>
            <thead>
              <tr>
                <th style="width: 100px;">Date</th>
                <th style="width: 120px;">Type & No.</th>
                <th>Particulars</th>
                <th class="text-right" style="width: 120px;">Debit</th>
                <th class="text-right" style="width: 120px;">Credit</th>
                <th class="text-right" style="width: 120px;">Balance</th>
              </tr>
            </thead>
            <tbody>
              <tr class="bold">
                <td colspan="3">Opening Balance</td>
                <td class="text-right">-</td>
                <td class="text-right">-</td>
                <td class="text-right">₹${openingBalance.toLocaleString(
                  'en-IN',
                  { minimumFractionDigits: 2 },
                )}</td>
              </tr>
              ${statementsWithBalance
                .map(
                  (entry) => `
                <tr>
                  <td>${formatDisplayDate(entry.date)}</td>
                  <td>${capitalizeFirstLetter(entry.entry_type)}<br>${
                    entry.transaction_number
                  }</td>
                  <td>${entry.opposite_account}${
                    entry.note
                      ? '<br><i style="font-size: 12px; color: #666;">' +
                        entry.note +
                        '</i>'
                      : ''
                  }</td>
                  <td class="text-right">${
                    entry.debit_credit === 'D'
                      ? '₹' +
                        parseFloat(entry.amount).toLocaleString('en-IN', {
                          minimumFractionDigits: 2,
                        })
                      : '-'
                  }</td>
                  <td class="text-right">${
                    entry.debit_credit === 'C'
                      ? '₹' +
                        parseFloat(entry.amount).toLocaleString('en-IN', {
                          minimumFractionDigits: 2,
                        })
                      : '-'
                  }</td>
                  <td class="text-right">₹${entry.runningBalance.toLocaleString(
                    'en-IN',
                    { minimumFractionDigits: 2 },
                  )}</td>
                </tr>
              `,
                )
                .join('')}
              <tr class="bold">
                <td colspan="3">Closing Balance</td>
                <td class="text-right">₹${totals.totalDebit.toLocaleString(
                  'en-IN',
                  { minimumFractionDigits: 2 },
                )}</td>
                <td class="text-right">₹${totals.totalCredit.toLocaleString(
                  'en-IN',
                  { minimumFractionDigits: 2 },
                )}</td>
                <td class="text-right">₹${totals.closingBalance.toLocaleString(
                  'en-IN',
                  { minimumFractionDigits: 2 },
                )}</td>
              </tr>
            </tbody>
          </table>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 250);
  };

  const handleExport = () => {
    const totals = getTotals();
    const statementsWithBalance = calculateRunningBalance();

    // Create CSV content
    let csvContent = '\uFEFF'; // UTF-8 BOM for Excel compatibility

    // Add header information
    csvContent += `Account Statement\n`;
    csvContent += `Account: ${accountName}\n`;
    csvContent += `Period: ${formatDisplayDate(
      startDate,
    )} to ${formatDisplayDate(endDate)}\n\n`;

    // Add table headers
    csvContent += 'Date,Type,Number,Particulars,Note,Debit,Credit,Balance\n';

    // Add opening balance
    csvContent += `"Opening Balance",,,,,,-,"₹${openingBalance.toLocaleString(
      'en-IN',
      { minimumFractionDigits: 2 },
    )}"\n`;

    // Add transaction rows
    statementsWithBalance.forEach((entry) => {
      const date = formatDisplayDate(entry.date);
      const type = capitalizeFirstLetter(entry.entry_type) || '';
      const number = entry.transaction_number || '';
      const particulars = (entry.opposite_account || '').replace(/"/g, '""'); // Escape quotes
      const note = (entry.note || '').replace(/"/g, '""');
      const debit =
        entry.debit_credit === 'D'
          ? `₹${parseFloat(entry.amount).toLocaleString('en-IN', {
              minimumFractionDigits: 2,
            })}`
          : '-';
      const credit =
        entry.debit_credit === 'C'
          ? `₹${parseFloat(entry.amount).toLocaleString('en-IN', {
              minimumFractionDigits: 2,
            })}`
          : '-';
      const balance = `₹${entry.runningBalance.toLocaleString('en-IN', {
        minimumFractionDigits: 2,
      })}`;

      csvContent += `"${date}","${type}","${number}","${particulars}","${note}","${debit}","${credit}","${balance}"\n`;
    });

    // Add closing balance
    csvContent += `"Closing Balance",,,,,"₹${totals.totalDebit.toLocaleString(
      'en-IN',
      { minimumFractionDigits: 2 },
    )}","₹${totals.totalCredit.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
    })}","₹${totals.closingBalance.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
    })}"\n`;

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      `Account_Statement_${accountName.replace(
        /[^a-z0-9]/gi,
        '_',
      )}_${formatDisplayDate(startDate)}_to_${formatDisplayDate(endDate)}.csv`,
    );
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Layout>
      <div
        style={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
          padding: '20px',
        }}
      >
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          {/* Header Section */}
          <div
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: '8px',
              padding: '12px 16px',
              marginBottom: '16px',
              boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '12px',
              }}
            >
              <div>
                <h2
                  style={{
                    color: 'white',
                    margin: 0,
                    fontSize: '1.25rem',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <FaFileInvoice size={18} /> Account Statement
                </h2>
              </div>
              <Button
                onClick={() => setShowModal(true)}
                style={{
                  background: 'white',
                  color: '#667eea',
                  border: 'none',
                  padding: '6px 12px',
                  borderRadius: '6px',
                  fontWeight: '500',
                  fontSize: '0.85rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow =
                    '0 4px 8px rgba(0,0,0,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                }}
              >
                <FaFilter /> Get Statement
              </Button>
            </div>
          </div>

          {/* Error Alert */}
          {error && (
            <Alert
              variant="danger"
              onClose={() => setError('')}
              dismissible
              style={{ borderRadius: '8px', marginBottom: '20px' }}
            >
              {error}
            </Alert>
          )}

          {/* Statement Display */}
          {statementFetched ? (
            <div
              className="account-statement-print"
              style={{
                background: 'white',
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                overflow: 'hidden',
              }}
            >
              {/* Print Header - Only visible when printing */}
              <div className="print-header" style={{ display: 'none' }}>
                <h3>Account Statement</h3>
                <p>
                  <strong>{accountName}</strong>
                </p>
                <p>
                  Period: {formatDisplayDate(startDate)} to{' '}
                  {formatDisplayDate(endDate)}
                </p>
              </div>
              {/* Account Info Header */}
              <div
                style={{
                  padding: '20px',
                  background: '#f8f9fa',
                  borderBottom: '2px solid #dee2e6',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '15px',
                  }}
                >
                  <div>
                    <h5
                      style={{
                        color: '#2c3e50',
                        margin: '0 0 8px 0',
                        fontWeight: '700',
                      }}
                    >
                      {accountName}
                    </h5>
                    <div
                      style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}
                    >
                      <span style={{ fontSize: '0.85rem', color: '#6c757d' }}>
                        <FaCalendarAlt style={{ marginRight: '5px' }} />
                        {formatDisplayDate(startDate)} to{' '}
                        {formatDisplayDate(endDate)}
                      </span>
                      <span style={{ fontSize: '0.85rem', color: '#6c757d' }}>
                        Transactions: {statement.length}
                      </span>
                    </div>
                  </div>
                  <div
                    className="no-print"
                    style={{ display: 'flex', gap: '10px' }}
                  >
                    <Button
                      variant="outline-primary"
                      size="sm"
                      style={{
                        borderRadius: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px',
                      }}
                      onClick={handlePrint}
                    >
                      <FaPrint /> Print
                    </Button>
                    <Button
                      variant="outline-success"
                      size="sm"
                      style={{
                        borderRadius: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px',
                      }}
                      onClick={handleExport}
                    >
                      <FaDownload /> Export
                    </Button>
                  </div>
                </div>
              </div>

              {/* Transactions Table */}
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th
                        style={{
                          padding: '12px 16px',
                          fontWeight: '600',
                          color: '#495057',
                          borderBottom: '2px solid #dee2e6',
                          textAlign: 'left',
                          fontSize: '0.85rem',
                          width: '100px',
                        }}
                      >
                        Date
                      </th>
                      <th
                        style={{
                          padding: '12px 16px',
                          fontWeight: '600',
                          color: '#495057',
                          borderBottom: '2px solid #dee2e6',
                          textAlign: 'left',
                          fontSize: '0.85rem',
                          width: '120px',
                        }}
                      >
                        Type & No.
                      </th>
                      <th
                        style={{
                          padding: '12px 16px',
                          fontWeight: '600',
                          color: '#495057',
                          borderBottom: '2px solid #dee2e6',
                          textAlign: 'left',
                          fontSize: '0.85rem',
                        }}
                      >
                        Particulars
                      </th>
                      <th
                        style={{
                          padding: '12px 16px',
                          fontWeight: '600',
                          color: '#495057',
                          borderBottom: '2px solid #dee2e6',
                          textAlign: 'right',
                          fontSize: '0.85rem',
                        }}
                      >
                        Debit
                      </th>
                      <th
                        style={{
                          padding: '12px 16px',
                          fontWeight: '600',
                          color: '#495057',
                          borderBottom: '2px solid #dee2e6',
                          textAlign: 'right',
                          fontSize: '0.85rem',
                        }}
                      >
                        Credit
                      </th>
                      <th
                        style={{
                          padding: '12px 16px',
                          fontWeight: '600',
                          color: '#495057',
                          borderBottom: '2px solid #dee2e6',
                          textAlign: 'right',
                          fontSize: '0.85rem',
                        }}
                      >
                        Balance
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Opening Balance Row */}
                    <tr>
                      <td
                        colSpan="3"
                        style={{
                          padding: '12px 16px',
                          borderBottom: '1px solid #dee2e6',
                          fontWeight: '600',
                          color: '#2c3e50',
                        }}
                      >
                        Opening Balance
                      </td>
                      <td
                        style={{
                          padding: '12px 16px',
                          borderBottom: '1px solid #dee2e6',
                          textAlign: 'right',
                        }}
                      >
                        -
                      </td>
                      <td
                        style={{
                          padding: '12px 16px',
                          borderBottom: '1px solid #dee2e6',
                          textAlign: 'right',
                        }}
                      >
                        -
                      </td>
                      <td
                        style={{
                          padding: '12px 16px',
                          borderBottom: '1px solid #dee2e6',
                          textAlign: 'right',
                          fontWeight: '600',
                          color: '#2c3e50',
                        }}
                      >
                        ₹
                        {openingBalance.toLocaleString('en-IN', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </td>
                    </tr>

                    {/* Transaction Rows */}
                    {statement.length > 0 ? (
                      calculateRunningBalance().map((entry, index) => (
                        <tr key={entry.id}>
                          <td
                            style={{
                              padding: '12px 16px',
                              borderBottom: '1px solid #e9ecef',
                              color: '#6c757d',
                              fontSize: '0.85rem',
                              whiteSpace: 'nowrap',
                              width: '100px',
                            }}
                          >
                            {formatDisplayDate(entry.date)}
                          </td>
                          <td
                            style={{
                              padding: '12px 16px',
                              borderBottom: '1px solid #e9ecef',
                              fontSize: '0.85rem',
                              width: '120px',
                            }}
                          >
                            <div
                              style={{
                                color: '#2c3e50',
                                fontWeight: '500',
                              }}
                            >
                              {capitalizeFirstLetter(entry.entry_type)}
                            </div>
                            <div
                              style={{
                                marginTop: '2px',
                                color: '#6c757d',
                                fontSize: '0.8rem',
                              }}
                            >
                              {entry.transaction_number}
                            </div>
                          </td>
                          <td
                            style={{
                              padding: '12px 16px',
                              borderBottom: '1px solid #e9ecef',
                              color: '#2c3e50',
                              fontSize: '0.85rem',
                            }}
                          >
                            <div>{entry.opposite_account}</div>
                            {entry.note && (
                              <div
                                style={{
                                  fontSize: '0.75rem',
                                  color: '#6c757d',
                                  marginTop: '2px',
                                  fontStyle: 'italic',
                                }}
                              >
                                {entry.note}
                              </div>
                            )}
                          </td>
                          <td
                            style={{
                              padding: '12px 16px',
                              borderBottom: '1px solid #e9ecef',
                              textAlign: 'right',
                              color:
                                entry.debit_credit === 'D'
                                  ? '#28a745'
                                  : '#6c757d',
                              fontWeight:
                                entry.debit_credit === 'D' ? '600' : 'normal',
                              fontSize: '0.85rem',
                            }}
                          >
                            {entry.debit_credit === 'D'
                              ? `₹${parseFloat(entry.amount).toLocaleString(
                                  'en-IN',
                                  {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  },
                                )}`
                              : '-'}
                          </td>
                          <td
                            style={{
                              padding: '12px 16px',
                              borderBottom: '1px solid #e9ecef',
                              textAlign: 'right',
                              color:
                                entry.debit_credit === 'C'
                                  ? '#dc3545'
                                  : '#6c757d',
                              fontWeight:
                                entry.debit_credit === 'C' ? '600' : 'normal',
                              fontSize: '0.85rem',
                            }}
                          >
                            {entry.debit_credit === 'C'
                              ? `₹${parseFloat(entry.amount).toLocaleString(
                                  'en-IN',
                                  {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  },
                                )}`
                              : '-'}
                          </td>
                          <td
                            style={{
                              padding: '12px 16px',
                              borderBottom: '1px solid #e9ecef',
                              textAlign: 'right',
                              fontWeight: '600',
                              color: '#2c3e50',
                              fontSize: '0.85rem',
                            }}
                          >
                            ₹
                            {entry.runningBalance.toLocaleString('en-IN', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan="6"
                          style={{
                            padding: '20px 16px',
                            textAlign: 'center',
                            color: '#6c757d',
                            fontSize: '0.9rem',
                            fontStyle: 'italic',
                            borderBottom: '1px solid #e9ecef',
                          }}
                        >
                          No transactions found for the selected period
                        </td>
                      </tr>
                    )}

                    {/* Totals Row */}
                    <tr style={{ fontWeight: '700' }}>
                      <td
                        colSpan="3"
                        style={{
                          padding: '12px 16px',
                          borderTop: '2px solid #dee2e6',
                          color: '#2c3e50',
                        }}
                      >
                        Closing Balance
                      </td>
                      <td
                        style={{
                          padding: '12px 16px',
                          borderTop: '2px solid #dee2e6',
                          textAlign: 'right',
                          color: '#28a745',
                        }}
                      >
                        ₹
                        {getTotals().totalDebit.toLocaleString('en-IN', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </td>
                      <td
                        style={{
                          padding: '12px 16px',
                          borderTop: '2px solid #dee2e6',
                          textAlign: 'right',
                          color: '#dc3545',
                        }}
                      >
                        ₹
                        {getTotals().totalCredit.toLocaleString('en-IN', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </td>
                      <td
                        style={{
                          padding: '12px 16px',
                          borderTop: '2px solid #dee2e6',
                          textAlign: 'right',
                          color: '#2c3e50',
                        }}
                      >
                        ₹
                        {getTotals().closingBalance.toLocaleString('en-IN', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div
              style={{
                textAlign: 'center',
                padding: '60px 20px',
                background: 'white',
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              }}
            >
              <FaFileInvoice
                style={{
                  fontSize: '4rem',
                  color: '#dee2e6',
                  marginBottom: '20px',
                }}
              />
              <h4 style={{ color: '#6c757d', marginBottom: '10px' }}>
                No Statement Available
              </h4>
              <p style={{ color: '#adb5bd' }}>
                Select an account and date range to view transactions
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Filter Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header
          closeButton
          style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            borderBottom: 'none',
          }}
        >
          <Modal.Title style={{ fontWeight: '700', fontSize: '1.25rem' }}>
            Filter Account Statement
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '30px' }}>
          {error && (
            <Alert variant="danger" onClose={() => setError('')} dismissible>
              {error}
            </Alert>
          )}
          <Form>
            <Form.Group
              className="mb-3"
              style={{ position: 'relative' }}
              ref={accountDropdownRef}
            >
              <Form.Label style={{ fontWeight: '600', color: '#495057' }}>
                Account <span style={{ color: '#dc3545' }}>*</span>
              </Form.Label>
              <Form.Control
                type="text"
                value={accountName}
                onChange={handleAccountChange}
                onFocus={() => setShowAccountDropdown(true)}
                placeholder="Type to search account..."
                style={{
                  borderRadius: '8px',
                  border: '1px solid #dee2e6',
                  padding: '10px 15px',
                  transition: 'all 0.3s ease',
                }}
              />
              {showAccountDropdown && filteredAccounts.length > 0 && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    marginTop: '4px',
                    background: 'white',
                    border: '1px solid #dee2e6',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    maxHeight: '200px',
                    overflowY: 'auto',
                    zIndex: 1000,
                  }}
                >
                  {filteredAccounts.map((acc) => (
                    <div
                      key={acc.id}
                      onClick={() => selectAccount(acc)}
                      style={{
                        padding: '10px 15px',
                        cursor: 'pointer',
                        transition: 'background 0.2s ease',
                        borderBottom: '1px solid #f8f9fa',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#f0f4ff';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'white';
                      }}
                    >
                      {acc.name}
                    </div>
                  ))}
                </div>
              )}
            </Form.Group>

            <div style={{ display: 'flex', gap: '15px', marginBottom: '15px' }}>
              <Form.Group style={{ flex: 1 }}>
                <Form.Label style={{ fontWeight: '600', color: '#495057' }}>
                  Start Date <span style={{ color: '#dc3545' }}>*</span>
                </Form.Label>
                <Form.Control
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  style={{
                    borderRadius: '8px',
                    border: '1px solid #dee2e6',
                    padding: '10px 15px',
                  }}
                />
              </Form.Group>
              <Form.Group style={{ flex: 1 }}>
                <Form.Label style={{ fontWeight: '600', color: '#495057' }}>
                  End Date <span style={{ color: '#dc3545' }}>*</span>
                </Form.Label>
                <Form.Control
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  style={{
                    borderRadius: '8px',
                    border: '1px solid #dee2e6',
                    padding: '10px 15px',
                  }}
                />
              </Form.Group>
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                gap: '10px',
                marginTop: '20px',
              }}
            >
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => setDateRange('today')}
                style={{ borderRadius: '6px', fontSize: '0.85rem' }}
              >
                Today
              </Button>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => setDateRange('thisMonth')}
                style={{ borderRadius: '6px', fontSize: '0.85rem' }}
              >
                This Month
              </Button>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => setDateRange('lastYear')}
                style={{ borderRadius: '6px', fontSize: '0.85rem' }}
              >
                Last FY
              </Button>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => setDateRange('thisFinancialYear')}
                style={{ borderRadius: '6px', fontSize: '0.85rem' }}
              >
                This FY
              </Button>
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer
          style={{ borderTop: '1px solid #dee2e6', padding: '20px 30px' }}
        >
          <Button
            variant="secondary"
            onClick={() => setShowModal(false)}
            disabled={loading}
            style={{
              borderRadius: '8px',
              padding: '10px 20px',
              fontWeight: '600',
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={fetchStatement}
            disabled={loading}
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              border: 'none',
              borderRadius: '8px',
              padding: '10px 20px',
              fontWeight: '600',
            }}
          >
            {loading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Fetching...
              </>
            ) : (
              'Fetch Statement'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Layout>
  );
};

export default AccountStatement;
